import { languages } from '@constants/languages'
import SearchEmpty from '@images/search_empty.svg'
import { useWizardContext } from '@pages/Landings/routes/Landings/CreateLandingWizard/contexts'
import {
  optionsWithAccessToThirdStep,
  secondStepOptions
} from '@pages/Landings/routes/Landings/CreateLandingWizard/helpers/constants'
import { Button, Heading, Message, Paragraph, Select, Spacer } from '@ui-kit'
import { TFunction } from 'i18next'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Row } from 'simple-flexbox'

import { PromptTextArea } from './components/PromptTextArea'
import styles from './Eigth.module.scss'

const getUserLanguage = (lang: string) => {
  const userLanguage = ['pl', 'pt', 'en'].includes(lang) ? lang : 'en'

  return userLanguage as 'pl' | 'pt' | 'en'
}

const EmptyMessage = () => {
  const { t } = useTranslation()

  return (
    <Message
      i18n={{
        title: t('message.empty.search.results'),
        message: t('message.empty.search.results.small.2')
      }}
      image={SearchEmpty}
      sizes={{
        imageHeight: 60,
        titleLevel: 4,
        messageSize: 16
      }}
    />
  )
}

const getGoalOptions = (t: TFunction<'translation', undefined>) => {
  const goalOptions = secondStepOptions.flatMap(option => {
    const services = t(`landings.create.wizard.goal.service`)
    const products = t(`landings.create.wizard.goal.product`)

    if (optionsWithAccessToThirdStep.includes(option.name)) {
      return [
        {
          value: `${option.name}-service`,
          label: `${t(`landings.create.wizard.goal.${option.name}`)} (${services})`
        },
        {
          value: `${option.name}-product`,
          label: `${t(`landings.create.wizard.goal.${option.name}`)} (${products})`
        }
      ]
    }

    return {
      value: option.name,
      label: t(`landings.create.wizard.goal.${option.name}`)
    }
  })

  return goalOptions
}

export const EightStep: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { validators, formik, randomizeContentPrompt } = useWizardContext()
  const { isSubmitting } = formik
  const { isEigthStepValid } = validators
  const [editedPrompt, setEditedPrompt] = useState(false)

  const [languagesOptions, setLanguagesOptions] = useState(
    languages.map(language => ({
      label: language[i18n.language as keyof typeof language],
      value: language.en
    }))
  )

  const goal = formik.values.contentGoal

  const goalOptions = getGoalOptions(t)

  const handleSearchLanguage = (value: string) => {
    const userLanguage = getUserLanguage(i18n.language)

    setLanguagesOptions(
      languages
        .filter(language =>
          language[userLanguage].toLowerCase().includes(value.toLowerCase())
        )
        .map(language => ({
          label: language[userLanguage],
          value: language.en
        }))
    )
  }

  const handleChangeLanguage = (value: string) => {
    formik.setFieldValue('contentLanguage', value)
  }

  const handleChangeContentGoal = async (value: string) => {
    formik.setFieldValue('contentGoal', value)

    if (!editedPrompt) {
      randomizeContentPrompt(value)
    }
  }

  return (
    <div className={styles['eight-step-container']}>
      <Heading level={1} weight={600}>
        {t('composer.content.ai.generation.describe.your.landing.page')}
      </Heading>
      <Spacer space={20} />
      <Paragraph size={14} color='neutral-6'>
        <Trans i18nKey='composer.content.ai.generation.the.more.detailed.description' />
      </Paragraph>

      <Spacer space={20} />

      <Paragraph size={14} color='neutral-6'>
        {t('composer.content.ai.generation.top.goal.of.your.lp')}
      </Paragraph>

      <Select
        id='contentGoal'
        placement='bottom-start'
        options={goalOptions}
        value={goal}
        i18n={{
          placeholder: t('composer.content.ai.generation.goal.label')
        }}
        onChange={handleChangeContentGoal}
      />

      <Spacer space={15} />

      <PromptTextArea setEditedPrompt={setEditedPrompt} />

      <Spacer space={15} />

      <label htmlFor='contentLanguage'>
        <Paragraph size={14} color='neutral-6'>
          {t('word.language')}
        </Paragraph>
      </label>

      <Select
        id='contentLanguage'
        data-testid='select-lp'
        placement='top-start'
        options={languagesOptions}
        value={formik.values.contentLanguage}
        i18n={{
          placeholder: t('word.language')
        }}
        onChange={handleChangeLanguage}
        searcher={{
          shouldRender: true,
          onChange: handleSearchLanguage,
          placeholder: t('word.search')
        }}
        emptyMessage={<EmptyMessage />}
      />

      <Spacer space={30} />

      <Row justifyContent='end'>
        <Button
          icon='icon-arrow-right'
          disabled={!isEigthStepValid || isSubmitting}
          type='submit'
          isLoading={isSubmitting}
        >
          {t('word.next')}
        </Button>
      </Row>
    </div>
  )
}
